import React, { useState, useEffect, useRef } from 'react';
import { X, Mic, MicOff, Volume2, VolumeX, Clock, AlertCircle, CheckCircle, Info } from 'lucide-react';

const VoiceDebugWindow = ({ isOpen, onClose, theme }) => {
  const [logs, setLogs] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [soundLevel, setSoundLevel] = useState(0);
  const [recognitionState, setRecognitionState] = useState('idle');
  const [streamState, setStreamState] = useState('disconnected');
  const [audioContextState, setAudioContextState] = useState('suspended');
  const [startTime, setStartTime] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [browserInfo, setBrowserInfo] = useState({});
  
  const logIdRef = useRef(0);
  const timerRef = useRef(null);

  // Browser detection
  useEffect(() => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isChrome = /Chrome/.test(navigator.userAgent);
    const isFirefox = /Firefox/.test(navigator.userAgent);
    
    setBrowserInfo({
      userAgent: navigator.userAgent,
      isSafari,
      isIOS,
      isChrome,
      isFirefox,
      hasWebkitSpeechRecognition: 'webkitSpeechRecognition' in window,
      hasSpeechRecognition: 'SpeechRecognition' in window,
      hasGetUserMedia: 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices,
      hasAudioContext: 'AudioContext' in window || 'webkitAudioContext' in window
    });
  }, []);

  // Timer for session duration
  useEffect(() => {
    if (isRecording && startTime) {
      timerRef.current = setInterval(() => {
        setSessionDuration(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording, startTime]);

  // Add log entry
  const addLog = (type, message, data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = {
      id: ++logIdRef.current,
      timestamp,
      type,
      message,
      data,
      duration: sessionDuration
    };
    
    setLogs(prev => [...prev.slice(-49), logEntry]); // Keep last 50 logs
  };

  // Clear logs
  const clearLogs = () => {
    setLogs([]);
    logIdRef.current = 0;
  };

  // Test microphone access
  const testMicrophone = async () => {
    addLog('info', 'Testing microphone access...');
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      setStreamState('connected');
      addLog('success', 'Microphone access granted', {
        tracks: stream.getTracks().length,
        trackStates: stream.getTracks().map(track => ({
          kind: track.kind,
          enabled: track.enabled,
          readyState: track.readyState
        }))
      });
      
      // Test audio context
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextClass();
      setAudioContextState(audioContext.state);
      
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
        setAudioContextState(audioContext.state);
        addLog('info', 'AudioContext resumed', { state: audioContext.state });
      }
      
      // Test audio analysis
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      
      analyser.fftSize = 256;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const checkSoundLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        const level = Math.min(100, (average / 255) * 100);
        setSoundLevel(level);
      };
      
      checkSoundLevel();
      addLog('success', 'Audio analysis working', { soundLevel: soundLevel.toFixed(1) });
      
      // Cleanup
      source.disconnect();
      stream.getTracks().forEach(track => track.stop());
      audioContext.close();
      
    } catch (error) {
      setStreamState('error');
      addLog('error', 'Microphone access failed', {
        error: error.name,
        message: error.message
      });
    }
  };

  // Test speech recognition
  const testSpeechRecognition = () => {
    addLog('info', 'Testing speech recognition...');
    
    if (!browserInfo.hasWebkitSpeechRecognition && !browserInfo.hasSpeechRecognition) {
      addLog('error', 'Speech recognition not supported');
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onstart = () => {
      setRecognitionState('listening');
      addLog('success', 'Speech recognition started');
    };
    
    recognition.onresult = (event) => {
      const transcript = event.results[event.results.length - 1][0].transcript;
      addLog('info', 'Speech detected', { transcript });
    };
    
    recognition.onerror = (event) => {
      setRecognitionState('error');
      addLog('error', 'Speech recognition error', { error: event.error });
    };
    
    recognition.onend = () => {
      setRecognitionState('ended');
      addLog('info', 'Speech recognition ended');
    };
    
    try {
      recognition.start();
      addLog('success', 'Speech recognition initiated');
    } catch (error) {
      addLog('error', 'Failed to start speech recognition', { error: error.message });
    }
  };

  // Start recording simulation
  const startRecording = () => {
    setIsRecording(true);
    setStartTime(Date.now());
    setSessionDuration(0);
    addLog('success', 'Recording started', { timestamp: new Date().toISOString() });
  };

  // Stop recording simulation
  const stopRecording = () => {
    setIsRecording(false);
    setStartTime(null);
    addLog('info', 'Recording stopped', { duration: sessionDuration });
  };

  // Get log icon
  const getLogIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle size={12} color="#10b981" />;
      case 'error': return <AlertCircle size={12} color="#ef4444" />;
      case 'warning': return <AlertCircle size={12} color="#f59e0b" />;
      default: return <Info size={12} color="#6b7280" />;
    }
  };

  // Get log color
  const getLogColor = (type) => {
    switch (type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: theme.cardBg || '#1f2937',
        border: `1px solid ${theme.primaryBorder || '#374151'}`,
        borderRadius: '16px',
        width: '90%',
        maxWidth: '1200px',
        height: '80%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${theme.primaryBorder || '#374151'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Mic size={24} color={theme.primaryText || '#ffffff'} />
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: theme.primaryText || '#ffffff',
              margin: 0
            }}>
              Voice Debug Console
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: theme.secondaryText || '#9ca3af',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Status Bar */}
        <div style={{
          padding: '16px 20px',
          background: theme.cardBg || '#1f2937',
          borderBottom: `1px solid ${theme.primaryBorder || '#374151'}`,
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isRecording ? <Mic size={16} color="#10b981" /> : <MicOff size={16} color="#6b7280" />}
            <span style={{ color: theme.primaryText || '#ffffff', fontSize: '14px' }}>
              Recording: {isRecording ? 'Active' : 'Inactive'}
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Volume2 size={16} color="#10b981" />
            <span style={{ color: theme.primaryText || '#ffffff', fontSize: '14px' }}>
              Sound Level: {soundLevel.toFixed(1)}%
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={16} color="#6b7280" />
            <span style={{ color: theme.primaryText || '#ffffff', fontSize: '14px' }}>
              Duration: {sessionDuration}s
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: theme.primaryText || '#ffffff', fontSize: '14px' }}>
              Recognition: {recognitionState}
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: theme.primaryText || '#ffffff', fontSize: '14px' }}>
              Stream: {streamState}
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: theme.primaryText || '#ffffff', fontSize: '14px' }}>
              AudioContext: {audioContextState}
            </span>
          </div>
        </div>

        {/* Browser Info */}
        <div style={{
          padding: '16px 20px',
          background: theme.cardBg || '#1f2937',
          borderBottom: `1px solid ${theme.primaryBorder || '#374151'}`,
          fontSize: '12px',
          color: theme.secondaryText || '#9ca3af'
        }}>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <span>Safari: {browserInfo.isSafari ? 'Yes' : 'No'}</span>
            <span>iOS: {browserInfo.isIOS ? 'Yes' : 'No'}</span>
            <span>Chrome: {browserInfo.isChrome ? 'Yes' : 'No'}</span>
            <span>Firefox: {browserInfo.isFirefox ? 'Yes' : 'No'}</span>
            <span>WebkitSpeech: {browserInfo.hasWebkitSpeechRecognition ? 'Yes' : 'No'}</span>
            <span>SpeechRecognition: {browserInfo.hasSpeechRecognition ? 'Yes' : 'No'}</span>
            <span>GetUserMedia: {browserInfo.hasGetUserMedia ? 'Yes' : 'No'}</span>
            <span>AudioContext: {browserInfo.hasAudioContext ? 'Yes' : 'No'}</span>
          </div>
        </div>

        {/* Controls */}
        <div style={{
          padding: '16px 20px',
          background: theme.cardBg || '#1f2937',
          borderBottom: `1px solid ${theme.primaryBorder || '#374151'}`,
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={testMicrophone}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Test Microphone
          </button>
          
          <button
            onClick={testSpeechRecognition}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Test Speech Recognition
          </button>
          
          <button
            onClick={isRecording ? stopRecording : startRecording}
            style={{
              background: isRecording 
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {isRecording ? 'Stop Recording' : 'Start Recording'}
          </button>
          
          <button
            onClick={clearLogs}
            style={{
              background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Clear Logs
          </button>
        </div>

        {/* Logs */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px 20px',
          background: theme.cardBg || '#1f2937'
        }}>
          {logs.length === 0 ? (
            <div style={{
              textAlign: 'center',
              color: theme.secondaryText || '#9ca3af',
              fontSize: '14px',
              padding: '40px'
            }}>
              No logs yet. Click "Test Microphone" or "Test Speech Recognition" to start.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {logs.map((log) => (
                <div
                  key={log.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '8px 12px',
                    background: theme.cardBg || '#1f2937',
                    border: `1px solid ${theme.primaryBorder || '#374151'}`,
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '120px' }}>
                    {getLogIcon(log.type)}
                    <span style={{ color: getLogColor(log.type), fontWeight: '600' }}>
                      {log.type.toUpperCase()}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '80px' }}>
                    <Clock size={12} color="#6b7280" />
                    <span style={{ color: theme.secondaryText || '#9ca3af' }}>
                      {log.timestamp}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '60px' }}>
                    <span style={{ color: theme.secondaryText || '#9ca3af' }}>
                      +{log.duration}s
                    </span>
                  </div>
                  
                  <div style={{ flex: 1, color: theme.primaryText || '#ffffff' }}>
                    {log.message}
                  </div>
                  
                  {log.data && (
                    <div style={{ color: theme.secondaryText || '#9ca3af', fontSize: '11px' }}>
                      {JSON.stringify(log.data, null, 2)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceDebugWindow;
